/* eslint-disable react/prop-types */
import React, { useMemo, useCallback } from "react";
import { Box, Typography, Chip, Tooltip, keyframes } from "@mui/material";
import { dateKey } from "layouts/attendance/utils/timeUtils";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CircleIcon from "@mui/icons-material/Circle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TodayIcon from "@mui/icons-material/Today";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import EventIcon from "@mui/icons-material/Event";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useEffect, useState } from "react";

// Animation keyframes
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const slideIn = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// Color configuration matching your specification
const statusColors = {
  full: {
    bg: "#4CAF50",
    bgLight: "#E8F5E9",
    text: "#FFFFFF",
    label: "Full",
    type: "full",
    icon: CheckCircleIcon,
    iconColor: "#4CAF50",
  },
  present: {
    bg: "#4CAF50",
    bgLight: "#E8F5E9",
    text: "#FFFFFF",
    label: "Present",
    type: "present",
    icon: CheckCircleIcon,
    iconColor: "#4CAF50",
  },
  absent: {
    bg: "#F44336",
    bgLight: "#FFEBEE",
    text: "#FFFFFF",
    label: "Absent",
    type: "absent",
    icon: ErrorIcon,
    iconColor: "#F44336",
  },
  holiday: {
    bg: "#FF9800",
    bgLight: "#FFF3E0",
    text: "#000000",
    label: "Holiday",
    type: "holiday",
    icon: BeachAccessIcon,
    iconColor: "#FF9800",
  },
  weekend: {
    bg: "#F97316",
    bgLight: "#FFEDD5",
    text: "#FFFFFF",
    label: "Weekend",
    type: "weekend",
    icon: BeachAccessIcon,
    iconColor: "#F97316",
  },
  half: {
    bg: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
    bgLight: "linear-gradient(90deg, #E8F5E9 50%, #FFEBEE 50%)",
    text: "#525253",
    label: "Half Day",
    iconColor: "#525253",

    icon: () => (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
          border: "1px solid #E5E7EB",
        }}
      />
    ),
  },

  // half_day: {
  //   bg: "linear-gradient(90deg, #4CAF50 50%, #F44336 50%)",
  //   bgLight: "linear-gradient(90deg, #E8F5E9 50%, #FFEBEE 50%)",
  //   text: "#FFFFFF",
  //   label: "Half Day",
  //   type: "half_day",
  //   icon: ScheduleIcon,
  //   iconColor: "#FF9800",
  // },
  leave: {
    bg: "#8B5CF6",
    bgLight: "#F3E5F5",
    text: "#FFFFFF",
    label: "Leave",
    type: "leave",
    icon: BeachAccessIcon,
    iconColor: "#8B5CF6",
  },
  half_day_leave: {
    bg: "linear-gradient(90deg, #8B5CF6 50%, #FFFFFF 50%)",
    bgLight: "linear-gradient(90deg, #F3E5F5 50%, #FFFFFF 50%)",
    text: "#FFFFFF",
    label: "Half Day Leave",
    type: "half_day_leave",
    icon: ScheduleIcon,
    iconColor: "#8B5CF6",
  },
  no_reason_declared: {
    bg: "#757575",
    bgLight: "#F5F5F5",
    text: "#FFFFFF",
    label: "No Reason",
    type: "no_reason_declared",
    icon: ErrorIcon,
    iconColor: "#757575",
  },
  logged_in: {
    bg: "#2196F3",
    bgLight: "#E3F2FD",
    text: "#FFFFFF",
    label: "Logged In",
    type: "logged_in",
    icon: PlayArrowIcon,
    iconColor: "#2196F3",
  },
  working_now: {
    bg: "#2196F3",
    bgLight: "#E3F2FD",
    text: "#FFFFFF",
    label: "Live",
    type: "working_now",
    icon: CircleIcon,
    iconColor: "#2196F3",
  },
  weekend_off: {
    bg: "#F97316",
    bgLight: "#FFEDD5",
    text: "#FFFFFF",
    label: "Weekend",
    type: "weekend",
    icon: BeachAccessIcon,
    iconColor: "#F97316",
  },
};

// Helper function to convert time string to minutes
const timeStrToMinutes = (timeStr, shiftStart = null) => {
  if (!timeStr) return null;

  const [h, m] = timeStr.split(":").map(Number);
  let minutes = h * 60 + m;

  // 🌙 Night shift fix
  if (shiftStart !== null && minutes < shiftStart) {
    minutes += 1440; // next day
  }

  return minutes;
};

// Format time display
const formatTime = (timeStr) => {
  if (!timeStr) return "";

  // If already Date object
  if (timeStr instanceof Date) {
    return timeStr.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // If number (minutes or timestamp)
  if (typeof timeStr === "number") {
    // assume minutes
    const h = Math.floor(timeStr / 60);
    const m = timeStr % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  // If string
  if (typeof timeStr === "string") {
    // handle "09:30:00" or "9:3"
    const parts = timeStr.split(":");
    const h = parts[0] ?? "00";
    const m = parts[1] ?? "00";
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }

  return "";
};

// Format time for timeline labels
const formatTimeShort = (minutes) => {
  const hours = Math.floor(minutes / 60) % 24;
  return `${hours}`;
};

// Individual Timeline Component
const TimelineVisualization = React.memo(({ dayData, isToday, currentTimeMinutes }) => {
  // Helper functions
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatTimeShort = (minutes) => {
    if (minutes == null) return '';
    const hours = Math.floor(minutes / 60) % 24;
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${hour12}${ampm}`;
  };

  const formatMinutesToTime = (minutes) => {
    if (minutes == null) return '';
    const hours = Math.floor(minutes / 60) % 24;
    const mins = minutes % 60;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;
    return `${hour12.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')} ${ampm}`;
  };

  const timeStrToMinutes = (timeStr, shiftStart = 0) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    let totalMinutes = hours * 60 + (minutes || 0);
    
    // Handle next day for night shifts
    if (shiftStart > 720 && totalMinutes < shiftStart - 720) {
      totalMinutes += 1440;
    }
    
    return totalMinutes;
  };

  const statusColors = {
    present: { bg: "#ECFDF5", bgLight: "rgba(236, 253, 245, 0.5)", border: "#A7F3D0", text: "#065F46", dot: "#10B981" },
    late: { bg: "#FFFBEB", bgLight: "rgba(255, 251, 235, 0.5)", border: "#FDE68A", text: "#92400E", dot: "#F59E0B" },
    absent: { bg: "#FEF2F2", bgLight: "rgba(254, 242, 242, 0.5)", border: "#FECACA", text: "#991B1B", dot: "#EF4444" },
    holiday: { bg: "#F5F3FF", bgLight: "rgba(245, 243, 255, 0.5)", border: "#DDD6FE", text: "#5B21B6", dot: "#8B5CF6" },
    weekend: { bg: "#F9FAFB", bgLight: "rgba(249, 250, 251, 0.5)", border: "#E5E7EB", text: "#4B5563", dot: "#6B7280" },
    working: { bg: "#EFF6FF", bgLight: "rgba(239, 246, 255, 0.5)", border: "#BFDBFE", text: "#1E40AF", dot: "#3B82F6" },
  };

  const blink = {
    "@keyframes blink": {
      "0%": { opacity: 1 },
      "50%": { opacity: 0.5 },
      "100%": { opacity: 1 },
    },
  };

  const pulse = {
    "@keyframes pulse": {
      "0%": { transform: "translate(-50%, -50%) scale(1)", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" },
      "50%": { transform: "translate(-50%, -50%) scale(1.2)", boxShadow: "0 3px 8px rgba(0,0,0,0.3)" },
      "100%": { transform: "translate(-50%, -50%) scale(1)", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" },
    },
  };

  // Get timeline bounds with padding (15 mins before shift, 15 mins after shift)
  const getTimelineBounds = useMemo(() => {
    if (!dayData?.shift_start || !dayData?.shift_end) {
      return {
        start: 0,
        end: 1440,
        shiftStart: 0,
        shiftEnd: 1440,
        paddedStart: 0,
        paddedEnd: 1440,
        duration: 1440
      };
    }

    const rawShiftStart = timeStrToMinutes(dayData.shift_start);
    let rawShiftEnd = timeStrToMinutes(dayData.shift_end);

    // Handle night shift
    if (rawShiftEnd <= rawShiftStart) {
      rawShiftEnd += 1440;
    }

    const shiftStart = rawShiftStart;
    const shiftEnd = rawShiftEnd;

    // Add 15 minutes padding before and after
    const paddedStart = Math.max(0, shiftStart - 15);
    const paddedEnd = shiftEnd + 15;
    const duration = paddedEnd - paddedStart;

    return { shiftStart, shiftEnd, paddedStart, paddedEnd, duration };
  }, [dayData]);

  const { shiftStart, shiftEnd, paddedStart, paddedEnd, duration } = getTimelineBounds;

  // Custom function to convert time to percentage based on padded range
  const minsToPct = (minutes) => {
    if (minutes == null || duration <= 0) return 0;
    
    // Handle times outside the padded range
    let adjustedMinutes = minutes;
    
    // For early check-ins (before padded start)
    if (minutes < paddedStart) {
      adjustedMinutes = paddedStart;
    }
    
    // For late check-outs (after padded end)
    if (minutes > paddedEnd) {
      adjustedMinutes = paddedEnd;
    }
    
    const pct = ((adjustedMinutes - paddedStart) / duration) * 100;
    return Math.min(99, Math.max(1, pct));
  };

  // Create time labels from padded start to padded end
  const timeLabels = useMemo(() => {
    const labels = [];
    const startHour = Math.floor(paddedStart / 60);
    const endHour = Math.ceil(paddedEnd / 60);
    
    for (let hour = startHour; hour <= endHour; hour++) {
      const timeInMinutes = hour * 60;
      if (timeInMinutes >= paddedStart && timeInMinutes <= paddedEnd) {
        labels.push({
          minutes: timeInMinutes,
          position: ((timeInMinutes - paddedStart) / duration) * 100,
          label: formatTimeShort(timeInMinutes),
          isShiftStart: Math.abs(timeInMinutes - shiftStart) < 30,
          isShiftEnd: Math.abs(timeInMinutes - shiftEnd) < 30,
        });
      }
    }
    return labels;
  }, [paddedStart, paddedEnd, duration, shiftStart, shiftEnd]);

  // Memoize timeline elements - Track work and break periods
  const timelineElements = useMemo(() => {
    const elements = [];
    if (!dayData) return elements;

    // Check-in (green dot) - always show actual time
    let inMinutes = null;
    if (dayData.in) {
      let inMinutesRaw = timeStrToMinutes(dayData.in, shiftStart);
      inMinutes = inMinutesRaw;
      
      elements.push({
        type: "check-in",
        time: inMinutes,
        label: `Check-in: ${formatTime(dayData.in)}`,
        color: "#4CAF50",
        isEarly: inMinutes < shiftStart,
      });
    }

    // Check-out (red dot)
    if (dayData.out) {
      const outMinutes = timeStrToMinutes(dayData.out, shiftStart);
      if (outMinutes !== null) {
        elements.push({
          type: "check-out",
          time: outMinutes,
          label: `Check-out: ${formatTime(dayData.out)}`,
          color: "#F44336",
          isLate: outMinutes > shiftEnd,
        });
      }
    }

    // Add shift indicator (light background)
    elements.push({
      type: "shift-indicator",
      start: shiftStart,
      end: shiftEnd,
      color: "rgba(59, 130, 246, 0.05)", // Very light blue
      label: `Shift: ${formatMinutesToTime(shiftStart)} - ${formatMinutesToTime(shiftEnd)}`,
    });

    // Early period indicator (15 min before shift)
    elements.push({
      type: "buffer-before",
      start: paddedStart,
      end: shiftStart,
      color: "rgba(16, 185, 129, 0.1)", // Very light green
      label: `15 min before shift`,
    });

    // Late period indicator (15 min after shift)
    elements.push({
      type: "buffer-after",
      start: shiftEnd,
      end: paddedEnd,
      color: "rgba(239, 68, 68, 0.1)", // Very light red
      label: `15 min after shift`,
    });

    // If early check-in, add early period line
    if (inMinutes !== null && inMinutes < shiftStart) {
      elements.push({
        type: "early-line",
        start: inMinutes,
        end: shiftStart,
        color: "#10B981",
        label: `Early: ${formatTime(dayData.in)} - Shift Start`,
        opacity: 0.3,
      });
    }

    // RED LINE: Late attendance (from shift start to check-in) - only if after shift
    if (inMinutes !== null && inMinutes > shiftStart) {
      elements.push({
        type: "late-line",
        start: shiftStart,
        end: inMinutes,
        color: "#F44336",
        label: `Late: ${formatTimeShort(shiftStart)} - ${formatTime(dayData.in)}`,
      });
    }

    // Process work periods with breaks
    if (dayData.in) {
      const inMinutesVal = timeStrToMinutes(dayData.in, shiftStart);
      let workPeriods = [];

      if (inMinutesVal !== null) {
        let currentStart = Math.max(inMinutesVal, shiftStart);

        let breaks = dayData.breaks || [];

        // Sort breaks by start time
        const sortedBreaks = [...breaks].sort(
          (a, b) => (timeStrToMinutes(a.start, shiftStart) || 0) - (timeStrToMinutes(b.start, shiftStart) || 0)
        );

        // Create work periods between breaks
        sortedBreaks.forEach((breakItem) => {
          const breakStart = timeStrToMinutes(breakItem.start, shiftStart);
          const breakEnd = timeStrToMinutes(breakItem.end, shiftStart);

          if (breakStart !== null && breakEnd !== null && breakStart > currentStart) {
            // Add work period before break
            if (breakStart > currentStart) {
              workPeriods.push({
                start: currentStart,
                end: breakStart,
                type: "work",
              });
            }

            // Add break period
            workPeriods.push({
              start: breakStart,
              end: breakEnd,
              type: "break",
            });

            currentStart = breakEnd;
          }
        });

        // Add final work period (after last break or check-out)
        let outMinutesRaw;

        if (dayData.out) {
          outMinutesRaw = timeStrToMinutes(dayData.out, shiftStart);
        } else if (isToday && dayData.attendance_status === "running") {
          outMinutesRaw = currentTimeMinutes;
        } else {
          outMinutesRaw = shiftEnd;
        }

        // Allow work to extend into buffer zone
        const outMinutes = Math.min(outMinutesRaw, paddedEnd);

        if (outMinutes > currentStart) {
          workPeriods.push({
            start: currentStart,
            end: outMinutes,
            type: "work",
          });
        } else if (currentStart > inMinutesVal) {
          // If we only have work before break and no end time
          workPeriods.push({
            start: inMinutesVal,
            end: currentStart,
            type: "work",
          });
        }

        // Convert work periods to timeline elements
        workPeriods.forEach((period, index) => {
          if (period.type === "work") {
            const start = Math.max(period.start, paddedStart);
            const end = Math.min(period.end, paddedEnd);

            if (end > start) {
              // Green work line
              elements.push({
                type: "work-line",
                start: start,
                end: end,
                color: "#4CAF50",
                isActive:
                  isToday &&
                  dayData.attendance_status === "running" &&
                  !dayData.out &&
                  period.end === outMinutes,
                label: `Work: ${formatTime(dayData.in)} - ${
                  period.end === outMinutes
                    ? dayData.out
                      ? formatTime(dayData.out)
                      : "Now"
                    : formatTimeShort(period.end)
                }`,
                periodIndex: index,
              });
            }
          } else if (period.type === "break") {
            const start = Math.max(period.start, paddedStart);
            const end = Math.min(period.end, paddedEnd);

            if (end > start) {
              // Yellow break line
              elements.push({
                type: "break-line",
                start: start,
                end: end,
                color: "#FF9800",
                label: `Break: ${formatTimeShort(period.start)} - ${formatTimeShort(
                  period.end
                )}`,
                periodIndex: index,
              });

              // Break start dot
              elements.push({
                type: "break-start",
                time: period.start,
                label: `Break Start: ${formatTimeShort(period.start)}`,
                color: "#FF9800",
              });

              // Break end dot
              elements.push({
                type: "break-end",
                time: period.end,
                label: `Break End: ${formatTimeShort(period.end)}`,
                color: "#FF9800",
              });
            }
          }
        });
      }
    }

    return elements.sort((a, b) => {
      const aStart = a.start || a.time || 0;
      const bStart = b.start || b.time || 0;
      return aStart - bStart;
    });
  }, [dayData, isToday, currentTimeMinutes, shiftStart, shiftEnd, paddedStart, paddedEnd]);

  const currentTimePct = isToday && currentTimeMinutes ? minsToPct(currentTimeMinutes) : null;

  // Check if early check-in
  const isEarlyCheckIn = dayData?.in && timeStrToMinutes(dayData.in, shiftStart) < shiftStart;
  
  // Check if late check-out
  const isLateCheckOut = dayData?.out && timeStrToMinutes(dayData.out, shiftStart) > shiftEnd;

  // Special states
  if (
    !dayData ||
    dayData.type === "weekend_off" ||
    dayData.type === "weekend" ||
    dayData.type === "holiday"
  ) {
    const dayType =
      dayData?.type === "weekend_off"
        ? "Weekend"
        : dayData?.type === "holiday"
        ? "Holiday"
        : dayData?.status === "absent"
        ? "Absent"
        : "No Data";
    const bgColor =
      dayData?.type === "weekend"
        ? statusColors.weekend.bgLight
        : dayData?.type === "holiday"
        ? statusColors.holiday.bgLight
        : dayData?.status === "absent"
        ? statusColors.absent.bgLight
        : "rgba(249, 250, 251, 0.8)";
    const borderColor =
      dayData?.type === "weekend"
        ? "#FFEDD5"
        : dayData?.type === "holiday"
        ? "#FFF3E0"
        : dayData?.status === "absent"
        ? "#FFEBEE"
        : "#E5E7EB";

    return (
      <Box
        sx={{
          height: "30px",
          bgcolor: bgColor,
          borderRadius: "4px",
          border: `1px dashed ${borderColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            fontSize: "10px",
            color: "#4B5563",
            fontWeight: 600,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {dayType}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        height: "30px",
        bgcolor: "#F8FAFC",
        borderRadius: "4px",
        border: "1px solid #E5E7EB",
        overflow: "visible",
        boxShadow: "0 0.5px 1px rgba(0, 0, 0, 0.02) inset",
        width: "100%",
        minWidth: "400px",
        px: "4px",
      }}
    >
      {/* Timeline grid with hour labels */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "4px",
          right: "4px",
          bottom: 0,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {/* Hour lines and labels */}
        {timeLabels.map((label, index) => {
          const isFirst = index === 0;
          const isLast = index === timeLabels.length - 1;

          return (
            <React.Fragment key={`time-label-${index}`}>
              {/* Vertical hour line */}
              <Box
                sx={{
                  position: "absolute",
                  left: `${label.position}%`,
                  top: "16px",
                  bottom: 0,
                  width: "1px",
                  bgcolor: label.isShiftStart || label.isShiftEnd ? "#3B82F6" : (isFirst || isLast ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.15)"),
                  zIndex: 1,
                }}
              />

              {/* Hour number label */}
              <Typography
                sx={{
                  position: "absolute",
                  left: `${label.position}%`,
                  top: "2px",
                  transform: "translateX(-50%)",
                  fontSize: "9px",
                  color: label.isShiftStart || label.isShiftEnd ? "#3B82F6" : (isFirst || isLast ? "#111827" : "#4B5563"),
                  fontWeight: label.isShiftStart || label.isShiftEnd ? 900 : 700,
                  backgroundColor: "white",
                  px: 0.5,
                  py: 0.125,
                  borderRadius: "2px",
                  border: label.isShiftStart || label.isShiftEnd ? "1px solid #3B82F6" : "0.5px solid rgba(0,0,0,0.08)",
                  zIndex: 2,
                  minWidth: "16px",
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 1px rgba(0,0,0,0.05)",
                }}
              >
                {label.label}
              </Typography>
            </React.Fragment>
          );
        })}
      </Box>

      {/* Buffer zone indicators */}
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          left: "4px",
          right: "4px",
          bottom: 0,
          zIndex: 2,
        }}
      >
        {/* Before shift buffer (light green) */}
        <Box
          sx={{
            position: "absolute",
            left: `${minsToPct(paddedStart)}%`,
            width: `${minsToPct(shiftStart) - minsToPct(paddedStart)}%`,
            top: "50%",
            transform: "translateY(-50%)",
            height: "4px",
            backgroundColor: "rgba(16, 185, 129, 0.1)",
            borderRadius: "2px 0 0 2px",
            zIndex: 2,
          }}
        />

        {/* After shift buffer (light red) */}
        <Box
          sx={{
            position: "absolute",
            left: `${minsToPct(shiftEnd)}%`,
            width: `${minsToPct(paddedEnd) - minsToPct(shiftEnd)}%`,
            top: "50%",
            transform: "translateY(-50%)",
            height: "4px",
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            borderRadius: "0 2px 2px 0",
            zIndex: 2,
          }}
        />

        {/* Shift indicator background (light blue) */}
        <Box
          sx={{
            position: "absolute",
            left: `${minsToPct(shiftStart)}%`,
            width: `${minsToPct(shiftEnd) - minsToPct(shiftStart)}%`,
            top: "50%",
            transform: "translateY(-50%)",
            height: "4px",
            backgroundColor: "rgba(59, 130, 246, 0.1)",
            borderLeft: "1px solid #3B82F6",
            borderRight: "1px solid #3B82F6",
            zIndex: 2,
          }}
        />
      </Box>

      {/* Work and Break Lines Container */}
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          left: "4px",
          right: "4px",
          bottom: 0,
          zIndex: 3,
        }}
      >
        {/* Early period line (green striped) */}
        {timelineElements.map((element, index) => {
          if (element.type === "early-line") {
            const startPct = minsToPct(element.start);
            const endPct = minsToPct(element.end);
            const widthPct = Math.max(0.5, endPct - startPct);

            return (
              <Tooltip key={`early-line-${index}`} title={element.label} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    height: "4px",
                    backgroundColor: element.color,
                    backgroundImage: `repeating-linear-gradient(
                      90deg,
                      transparent,
                      transparent 2px,
                      rgba(255,255,255,0.5) 2px,
                      rgba(255,255,255,0.5) 4px
                    )`,
                    borderRadius: "2px",
                    zIndex: 2,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    opacity: 0.5,
                  }}
                />
              </Tooltip>
            );
          }
          return null;
        })}

        {/* RED LINE: Late attendance */}
        {timelineElements.map((element, index) => {
          if (element.type === "late-line") {
            const startPct = minsToPct(element.start);
            const endPct = minsToPct(element.end);
            const widthPct = Math.max(0.5, endPct - startPct);

            return (
              <Tooltip key={`late-line-${index}`} title={element.label} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    height: "4px",
                    backgroundColor: element.color,
                    backgroundImage: `repeating-linear-gradient(
                      90deg,
                      transparent,
                      transparent 3px,
                      rgba(255,255,255,0.3) 3px,
                      rgba(255,255,255,0.3) 6px
                    )`,
                    borderRadius: "2px",
                    zIndex: 3,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "-1px",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: element.color,
                      border: "1px solid white",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 0,
                      top: "-1px",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: element.color,
                      border: "1px solid white",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    },
                  }}
                />
              </Tooltip>
            );
          }
          return null;
        })}

        {/* Work lines (green) */}
        {timelineElements.map((element, index) => {
          if (element.type === "work-line") {
            const startPct = minsToPct(element.start);
            const endPct = minsToPct(element.end);
            const widthPct = Math.max(0.5, endPct - startPct);

            return (
              <Tooltip key={`work-line-${index}`} title={element.label} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    height: "4px",
                    backgroundColor: element.color,
                    backgroundImage: `repeating-linear-gradient(
                      90deg,
                      transparent,
                      transparent 3px,
                      rgba(255,255,255,0.3) 3px,
                      rgba(255,255,255,0.3) 6px
                    )`,
                    borderRadius: "2px",
                    zIndex: 4,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "-1px",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: element.color,
                      border: "1px solid white",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      right: 0,
                      top: "-1px",
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: element.color,
                      border: "1px solid white",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    },
                    ...(element.isActive && {
                      animation: `${blink} 2s ease-in-out infinite`,
                    }),
                  }}
                />
              </Tooltip>
            );
          }
          return null;
        })}

        {/* Break lines (yellow) */}
        {timelineElements.map((element, index) => {
          if (element.type === "break-line") {
            const startPct = minsToPct(element.start);
            const endPct = minsToPct(element.end);
            const widthPct = Math.max(0.5, endPct - startPct);

            return (
              <Tooltip key={`break-line-${index}`} title={element.label} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    left: `${startPct}%`,
                    width: `${widthPct}%`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    height: "6px",
                    background: `repeating-linear-gradient(
                      90deg,
                      ${element.color},
                      ${element.color} 4px,
                      transparent 4px,
                      transparent 8px
                    )`,
                    borderRadius: "3px",
                    zIndex: 5,
                    opacity: 0.9,
                    border: "1px solid rgba(255,255,255,0.3)",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                />
              </Tooltip>
            );
          }
          return null;
        })}
      </Box>

      {/* Dots Container */}
      <Box
        sx={{
          position: "absolute",
          top: "16px",
          left: "4px",
          right: "4px",
          bottom: 0,
          zIndex: 6,
        }}
      >
        {/* Check-in, Check-out, and Break dots */}
        {timelineElements.map((element, index) => {
          if (
            element.type === "check-in" ||
            element.type === "check-out" ||
            element.type === "break-start" ||
            element.type === "break-end"
          ) {
            // For check-in dots that are before shift, show at actual position
            let timePct;
            if (element.type === "check-in" && element.time < shiftStart) {
              timePct = ((element.time - paddedStart) / duration) * 100;
            } else {
              timePct = minsToPct(element.time);
            }
            
            timePct = Math.min(98, Math.max(2, timePct));

            let tooltipLabel = element.label;
            let dotColor = element.color;
            let dotSize = element.type === "check-in" ? 10 : 8;
            let dotBorder = "2px solid white";

            if (element.type === "check-in") {
              dotColor = "#4CAF50";
              tooltipLabel = `Check-in: ${formatTime(dayData.in)}`;
            } else if (element.type === "check-out") {
              dotColor = "#F44336";
              tooltipLabel = `Check-out: ${formatTime(dayData.out)}`;
            } else if (element.type === "break-start") {
              dotColor = "#FF9800";
              tooltipLabel = `Break started at ${formatMinutesToTime(element.time)}`;
            } else if (element.type === "break-end") {
              dotColor = "#FF9800";
              tooltipLabel = `Break ended at ${formatMinutesToTime(element.time)}`;
            }

            return (
              <Tooltip key={`${element.type}-${index}`} title={tooltipLabel} arrow>
                <Box
                  sx={{
                    position: "absolute",
                    left: `${timePct}%`,
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 6,
                    width: `${dotSize}px`,
                    height: `${dotSize}px`,
                    backgroundColor: dotColor,
                    borderRadius: "50%",
                    border: dotBorder,
                    boxShadow: `0 2px 4px ${dotColor}80`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translate(-50%, -50%) scale(1.3)",
                      boxShadow: `0 3px 8px ${dotColor}`,
                    },
                    ...(element.type === "check-in" && {
                      animation: `${pulse} 2s ease-in-out infinite`,
                    }),
                  }}
                />
              </Tooltip>
            );
          }
          return null;
        })}

        {/* Current time indicator for today */}
        {isToday && currentTimePct !== null && currentTimePct >= 0 && currentTimePct <= 100 && (
          <Tooltip
            title={`Current Time: ${new Date().getHours().toString().padStart(2, "0")}:${new Date()
              .getMinutes()
              .toString()
              .padStart(2, "0")}`}
            arrow
          >
            <Box
              sx={{
                position: "absolute",
                left: `${currentTimePct}%`,
                top: 0,
                bottom: 0,
                width: "2px",
                bgcolor: "#F97316",
                zIndex: 7,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-2px",
                  left: "-3px",
                  width: "8px",
                  height: "8px",
                  bgcolor: "#F97316",
                  borderRadius: "50%",
                  border: "2px solid white",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                  animation: `${pulse} 1.2s ease-in-out infinite`,
                },
                "&::after": {
                  content: '"NOW"',
                  position: "absolute",
                  top: "-18px",
                  left: "-12px",
                  fontSize: "8px",
                  fontWeight: 900,
                  color: "#F97316",
                  backgroundColor: "white",
                  px: 0.5,
                  py: 0.125,
                  borderRadius: "2px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
                  border: "1px solid #FFEDD5",
                  letterSpacing: "0.05em",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Early check-in indicator badge */}
      {isEarlyCheckIn && (
        <Box
          sx={{
            position: "absolute",
            top: "-8px",
            left: "8px",
            backgroundColor: "#10B981",
            borderRadius: "10px",
            px: 1,
            py: 0.25,
            zIndex: 10,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Typography
            sx={{
              fontSize: "8px",
              color: "white",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            EARLY
          </Typography>
        </Box>
      )}
    </Box>
  );
});
// Main EnhancedDayRow Component
function EnhancedDayRow({
  day,
  date,
  entry,
  timelineData,
  currentTimeMinutes,
  onDayClick,
  isToday,
  currentDayData,
  user,
}) {
  const dateStr = dateKey(entry.date);
  // Memoize day data
  const dayData = useMemo(
    () => timelineData?.find((item) => item.date === dateStr),
    [timelineData, dateStr]
  );

  const statusConfig = useMemo(() => {
    // 0️⃣ NO DATA (status empty / null / undefined)
    if (
      !dayData ||
      dayData.status === undefined ||
      dayData.status === null ||
      dayData.status === ""
    ) {
      return {
        bg: "#757575",
        bgLight: "#F5F5F5",
        text: "#FFFFFF",
        label: "No Data",
        icon: ErrorIcon,
        iconColor: "#9CA3AF",
      };
    }

    // 1️⃣ LIVE WORKING (highest priority)
    if (isToday && dayData.attendance_status === "running") {
      return statusColors.working_now;
    }

    // 2️⃣ FULL DAY
    if (dayData.status === "full") {
      return statusColors.full;
    }

    // 3️⃣ HALF DAY
    if (dayData.status === "half") {
      return statusColors.half;
    }

    // 4️⃣ ABSENT
    if (dayData.status === "absent") {
      return statusColors.absent;
    }

    // 5️⃣ LEAVE
    if (dayData.type === "leave") {
      return dayData.status == "half" ? statusColors.half_day_leave : statusColors.leave;
    }

    // 6️⃣ WEEKEND / HOLIDAY
    if (dayData.status === "weekend_off" || dayData.type === "weekend") {
      return statusColors.weekend_off;
    }
    if (dayData.type === "holiday") return statusColors.holiday;

    // 7️⃣ LOGGED IN BUT NOT RUNNING
    if (dayData.attendance_status === "logged_in") {
      return statusColors.logged_in;
    }

    // 8️⃣ FINAL FALLBACK → NO DATA
    return {
      bg: "#757575",
      bgLight: "#F5F5F5",
      text: "#FFFFFF",
      label: "No Data",
      icon: ErrorIcon,
      iconColor: "#9CA3AF",
    };
  }, [dayData, isToday]);

  const [liveMinutes, setLiveMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });

  const StatusIcon = statusConfig.icon || CircleIcon;

  // Get check-in and check-out times
  const checkInTime = useMemo(() => (dayData?.in ? formatTime(dayData.in) : null), [dayData]);

  const checkOutTime = useMemo(() => (dayData?.out ? formatTime(dayData.out) : null), [dayData]);

  const currentTime = useMemo(
    () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    []
  );

  useEffect(() => {
    if (!isToday || dayData?.attendance_status !== "running") return;

    const interval = setInterval(() => {
      const now = new Date();
      setLiveMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60 * 1000); // update every minute

    return () => clearInterval(interval);
  }, [isToday, dayData?.attendance_status]);

  const liveTimeLabel = useMemo(() => {
    const h = Math.floor(liveMinutes / 60);
    const m = liveMinutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }, [liveMinutes]);

  const handleClick = useCallback(
    (e) => {
      e.preventDefault();
      onDayClick({
        date: entry.date,
        user: user || currentDayData?.user, // ya parent se pass karo
      });
    },
    [onDayClick, entry.date, currentDayData]
  );

  const attendanceBreakTime = useMemo(() => {
    return dayData?.attendance_break_minutes;
  }, [dayData]);

  return (
    <Box
      component="article"
      sx={{
        display: "flex",
        alignItems: "stretch",
        minHeight: "75px", // Increased height for timeline
        bgcolor: isToday ? statusConfig.bgLight : "#FFFFFF",
        borderRadius: "8px",
        mb: 1.5,
        border: `1.5px solid ${isToday ? `${statusConfig.iconColor}25` : "#E5E7EB"}`,
        boxShadow: isToday
          ? `0 2px 8px ${statusConfig.iconColor}10, 0 1px 2px rgba(0,0,0,0.04)`
          : "0 1px 3px rgba(0, 0, 0, 0.03)",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        position: "relative",
        animation: `${slideIn} 0.3s ease-out`,
        "&:hover": {
          transform: "translateY(-1px)",
          bgcolor: isToday ? `${statusConfig.iconColor}06` : "#F9FAFB",
          borderColor: isToday ? `${statusConfig.iconColor}40` : "#D1D5DB",
          boxShadow: isToday
            ? `0 8px 20px ${statusConfig.iconColor}15, 0 2px 6px rgba(0,0,0,0.06)`
            : "0 8px 20px rgba(0, 0, 0, 0.06)",
        },
        "&::before": isToday
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: statusConfig.bg,
              zIndex: 1,
              opacity: 0.8,
            }
          : {},
      }}
      onClick={handleClick}
    >
      {/* Day Column */}
      <Box
        sx={{
          width: "70px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 1.5,
          borderRight: "1px solid rgba(0, 0, 0, 0.04)",
          bgcolor: isToday ? `${statusConfig.iconColor}06` : "#F9FAFB",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {isToday && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 0,
              height: 0,
              borderTop: "20px solid",
              borderTopColor: statusConfig.iconColor,
              borderLeft: "20px solid transparent",
            }}
          >
            <TodayIcon
              sx={{
                position: "absolute",
                top: "-18px",
                right: "4px",
                fontSize: "10px",
                color: "white",
                fontWeight: "bold",
              }}
            />
          </Box>
        )}
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 800,
            color: isToday ? statusConfig.iconColor : "#6B7280",
            mb: 0.5,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {day.substring(0, 3)}
        </Typography>
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 900,
            color: isToday ? statusConfig.iconColor : "#111827",
            lineHeight: 1,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {date}
        </Typography>
      </Box>

      {/* Status Column */}
      <Box
        sx={{
          width: "120px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          p: 1.5,
          borderRight: "1px solid rgba(0, 0, 0, 0.04)",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              bgcolor: statusConfig.bgLight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1.5px solid ${statusConfig.iconColor}40`,
            }}
          >
            <StatusIcon sx={{ fontSize: 14, color: statusConfig.iconColor }} />
          </Box>
          <Chip
            label={statusConfig.label}
            size="small"
            sx={{
              bgcolor: statusConfig.bg,
              color: statusConfig.text,
              fontWeight: 800,
              fontSize: "10px",
              height: "22px",
              borderRadius: "12px",
              px: 1,
              "& .MuiChip-label": {
                px: 0.5,
              },
            }}
          />
        </Box>
      </Box>

      {/* Timeline Column */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          p: 1.5,
          gap: 1.25, // Increased gap for timeline
          overflow: "hidden",
        }}
      >
        <TimelineVisualization
          dayData={dayData}
          isToday={isToday}
          currentTimeMinutes={liveMinutes}
        />

        {/* Time details - Compact */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            flexWrap: "wrap",
            mt: 0.5,
          }}
        >
          {checkInTime && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: "#E8F5E9",
                border: "1px solid #4CAF5030",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: "#4CAF50",
                  borderRadius: "50%",
                  border: "1px solid white",
                  boxShadow: "0 0 0 1px #4CAF50",
                }}
              />
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#065F46",
                  fontWeight: 700,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {checkInTime}
              </Typography>
              <Typography
                sx={{
                  fontSize: "9px",
                  color: "#6B7280",
                  fontWeight: 600,
                }}
              >
                IN
              </Typography>
            </Box>
          )}

          {checkOutTime && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: "#FFEBEE",
                border: "1px solid #F4433630",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: "#F44336",
                  borderRadius: "50%",
                  border: "1px solid white",
                  boxShadow: "0 0 0 1px #F44336",
                }}
              />
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#991B1B",
                  fontWeight: 700,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {checkOutTime}
              </Typography>
              <Typography
                sx={{
                  fontSize: "9px",
                  color: "#6B7280",
                  fontWeight: 600,
                }}
              >
                OUT
              </Typography>
            </Box>
          )}

          {Number(attendanceBreakTime) > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: "#FFF7ED",
                border: "1px solid #F59E0B40",
              }}
            >
              <PauseIcon sx={{ fontSize: 10, color: "#F59E0B" }} />
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#92400E",
                  fontWeight: 800,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {attendanceBreakTime}m
              </Typography>
              <Typography
                sx={{
                  fontSize: "9px",
                  color: "#6B7280",
                  fontWeight: 600,
                }}
              >
                BREAK
              </Typography>
            </Box>
          )}

          {isToday && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: "auto",
                px: 0.75,
                py: 0.25,
                borderRadius: "4px",
                bgcolor: "#E3F2FD",
                border: "1px solid #2196F330",
              }}
            >
              <AccessTimeIcon sx={{ fontSize: 11, color: "#2196F3" }} />
              <Typography
                sx={{
                  fontSize: "11px",
                  color: "#1E40AF",
                  fontWeight: 700,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {liveTimeLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// Export memoized component
export default React.memo(EnhancedDayRow);
